import React, { useMemo, useState } from "react";
import styles from "./FilterBar.module.scss";
import DropdownContainer from "../../../../components/dropdownContainer/DropdownContainer";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import {
  selectAmenitiesList,
  selectBorderColor,
  selectColor,
  selectHotelTypeList,
} from "../../../../redux/slices/enviromentSlice/EnviromentSlice.selectors";
import FilterComponent from "./components/filterComponent/FilterComponent";

import CustomCheckbox from "../../../../components/customCheckbox/CustomCheckbox";
import PriceSlider from "./components/priceSlider/PriceSlider";
import colors from "../../../../styles/variables.module.scss";
import styled from "styled-components";
import tinycolor from "tinycolor2";
import defaultStyles from "../../../../styles/variables.module.scss";
import useOnResize from "../../../../hooks/useOnResize";
import { FilterSortType, filterStateI } from "../../contexts/filterContext";

const mediumBreakpoint = 425;

const List = styled.li<{
  mainColor: string;
  onClick: () => void;
}>`
  &:hover {
    background-color: ${(props: any) =>
      props.mainColor ?? colors.primaryBlue} !important;
    color: ${(props: any) =>
      tinycolor(props.mainColor).isDark() ? "#ffffff" : "#000000"} !important;
  }
`;

const FilterBar = (props: {
  setFilterState: Function;
  filterState: filterStateI;
  isMapviewOpen: boolean;
  setIsMapviewOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  // redux hooks
  const amenitiesList = useAppSelector(selectAmenitiesList);
  const hotelTypeList = useAppSelector(selectHotelTypeList);
  const borderColor = useAppSelector(selectBorderColor);
  const mainColor = useAppSelector(selectColor) || defaultStyles.primaryBlue;

  const { setFilterState, filterState, isMapviewOpen, setIsMapviewOpen } =
    props;
  const [openDropdown, setOpenDropdown] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isBiggerThanM, setIsBiggerThanM] = useState(
    mediumBreakpoint < window.innerWidth
  );

  useOnResize(() => {
    setIsBiggerThanM(mediumBreakpoint < window.innerWidth);
  });

  const { t } = useTranslation();

  const handleOpenDropdown = (
    event: React.MouseEvent,
    callback: Function,
    state: boolean
  ) => {
    event.preventDefault();
    callback(!state);
  };

  const filters = useMemo(
    () => [
      {
        name: "filters.price",
        content: (
          <div
            onClick={(event) => event.stopPropagation()}
            className={styles.sliderDropdownContainer}
          >
            <PriceSlider
              setFilterState={setFilterState}
              filterState={filterState}
            />
          </div>
        ),
        shouldExpand: true,
      },
      {
        name: "filters.propertyType",
        content: (
          <ul onClick={(event) => event.stopPropagation()}>
            {hotelTypeList.map((facility) => {
              return (
                <li key={facility.id}>
                  <CustomCheckbox
                    name={facility.id}
                    label={facility.name}
                    checked={filterState.hotelTypeIds.some(
                      (type) => type === facility.id
                    )}
                    onChange={(checked) => {
                      if (checked)
                        setFilterState((currentFilterState: filterStateI) => ({
                          ...currentFilterState,
                          hotelTypeIds: [
                            ...currentFilterState.hotelTypeIds,
                            facility.id,
                          ],
                        }));

                      if (!checked)
                        setFilterState((currentFilterState: filterStateI) => ({
                          ...currentFilterState,
                          hotelTypeIds: [
                            ...currentFilterState.hotelTypeIds.filter(
                              (id) => id !== facility.id
                            ),
                          ],
                        }));
                    }}
                  />
                </li>
              );
            })}
          </ul>
        ),
      },
      {
        name: "filters.facilities",
        content: (
          <ul onClick={(event) => event.stopPropagation()}>
            {amenitiesList.map((facility) => {
              return (
                <li key={facility.id}>
                  <CustomCheckbox
                    name={facility.id}
                    label={facility.name}
                    checked={filterState.amenitiesIds.some(
                      (type) => type === facility.id
                    )}
                    onChange={(checked) => {
                      if (checked)
                        setFilterState((currentFilterState: filterStateI) => ({
                          ...currentFilterState,
                          amenitiesIds: [
                            ...currentFilterState.amenitiesIds,
                            facility.id,
                          ],
                        }));

                      if (!checked)
                        setFilterState((currentFilterState: filterStateI) => ({
                          ...currentFilterState,
                          amenitiesIds: [
                            ...currentFilterState.amenitiesIds.filter(
                              (id) => id !== facility.id
                            ),
                          ],
                        }));
                    }}
                  />
                </li>
              );
            })}
          </ul>
        ),
        shouldExpand: true,
      },
      {
        name:
          filterState.isFreeCancelable === FilterSortType.UNSET
            ? "filters.policies"
            : filterState.isFreeCancelable
            ? "filters.refundable"
            : "filters.noRefundable",
        content: (
          <div className={styles.filterPrice}>
            <ul>
              <List
                mainColor={mainColor}
                className={styles.liOption}
                onClick={() =>
                  setFilterState((prevState: filterStateI) => ({
                    ...prevState,
                    isFreeCancelable: FilterSortType.UNSET,
                  }))
                }
              >
                {t("filters.allPrices")}
              </List>
              <List
                mainColor={mainColor}
                className={styles.liOption}
                onClick={() =>
                  setFilterState((prevState: filterStateI) => ({
                    ...prevState,
                    isFreeCancelable: true,
                  }))
                }
              >
                {t("filters.refundable")}
              </List>
              <List
                mainColor={mainColor}
                className={styles.liOption}
                onClick={() =>
                  setFilterState((prevState: filterStateI) => ({
                    ...prevState,
                    isFreeCancelable: false,
                  }))
                }
              >
                {t("filters.noRefundable")}
              </List>
            </ul>
          </div>
        ),
      },
      {
        name:
          filterState.orderBy === FilterSortType.ASCENDING
            ? "filters.cheapestPrice"
            : filterState.orderBy === FilterSortType.DESCENDING
            ? "filters.higherPrice"
            : "filters.orderBy",
        content: (
          <div className={styles.filterPrice}>
            <ul>
              <List
                mainColor={mainColor}
                className={styles.liOption}
                onClick={() =>
                  setFilterState((prevState: filterStateI) => ({
                    ...prevState,
                    orderBy: FilterSortType.DESCENDING,
                  }))
                }
              >
                {t("filters.higherPrice")}
              </List>
              <List
                mainColor={mainColor}
                className={styles.liOption}
                onClick={() =>
                  setFilterState((prevState: filterStateI) => ({
                    ...prevState,
                    orderBy: FilterSortType.ASCENDING,
                  }))
                }
              >
                {t("filters.cheapestPrice")}
              </List>
            </ul>
          </div>
        ),
      },
    ],
    [amenitiesList, filterState, hotelTypeList, mainColor, setFilterState, t]
  );

  return (
    <>
      <div className={styles.mainContainer}>
        <div className={`${styles.posRelative} ${styles.row}`}>
          <div
            onClick={(e) => {
              handleOpenDropdown(e, setOpenDropdown, openDropdown);
            }}
            className={styles.filterDropdown_menu}
            style={{ borderColor: borderColor || colors.borderLightgray }}
          >
            <div className={styles.filterDropdown_selected}>
              {t("labels.filters")}
            </div>
            <div
              className={`${styles.filterDropdown_iconContainer} ${
                openDropdown && styles.dropdownIconOpen
              }`}
            >
              <img
                src="https://booking.availroom.com/v2/assets/icons/dropdown.svg"
                alt=""
              />
            </div>
          </div>
          <div
            className={styles.filterDropdown}
            style={{ borderColor: borderColor || colors.borderLightgray }}
          >
            <div
              className={`${styles.filterDropdown_selected} ${styles.no_wrap}`}
            >
              {t("filters.mapView")}
            </div>
            <div className={styles.filterDropdown_iconContainer}>
              <label className={styles.switch}>
                <input checked={isMapviewOpen} readOnly type="checkbox" />
                <div
                  className={styles.slider}
                  onClick={() => {
                    setIsMapviewOpen(!isMapviewOpen);
                  }}
                  style={
                    isMapviewOpen
                      ? {
                          backgroundColor: mainColor || colors.primaryBlue,
                        }
                      : { backgroundColor: colors.borderLightgray }
                  }
                ></div>
              </label>
            </div>
          </div>
          {openDropdown && (
            <div className={styles.dropdownSelectContainer}>
              <DropdownContainer onClickOutside={() => setOpenDropdown(false)}>
                {amenitiesList.length !== 0
                  ? filters.map(({ name, content, shouldExpand }) => (
                      <FilterComponent
                        styles={styles}
                        key={name}
                        name={name}
                        shouldExpand={shouldExpand}
                      >
                        {content}
                      </FilterComponent>
                    ))
                  : filters
                      .filter((filter) => filter.name !== "filters.facilities")
                      .map(({ name, content, shouldExpand }) => (
                        <FilterComponent
                          styles={styles}
                          key={name}
                          name={name}
                          shouldExpand={shouldExpand}
                        >
                          {content}
                        </FilterComponent>
                      ))}
              </DropdownContainer>
            </div>
          )}
        </div>

        <div className={styles.filterContainer}>
          {amenitiesList.length !== 0
            ? filters.map(({ name, content, shouldExpand }) => (
                <FilterComponent
                  styles={styles}
                  key={name}
                  name={name}
                  shouldExpand={shouldExpand}
                >
                  {content}
                </FilterComponent>
              ))
            : filters
                .filter((filter) => filter.name !== "filters.facilities")
                .map(({ name, content, shouldExpand }) => (
                  <FilterComponent
                    styles={styles}
                    key={name}
                    name={name}
                    shouldExpand={shouldExpand}
                  >
                    {content}
                  </FilterComponent>
                ))}

          <div
            className={styles.filterDropdown}
            style={{ borderColor: borderColor || colors.borderLightgray }}
          >
            <div
              className={`${styles.filterDropdown_selected} ${styles.no_wrap}`}
            >
              {t("filters.mapView")}
            </div>
            <div className={styles.filterDropdown_iconContainer}>
              <label className={styles.switch}>
                <input checked={isMapviewOpen} readOnly type="checkbox" />
                <div
                  className={styles.slider}
                  onClick={() => {
                    setIsMapviewOpen(!isMapviewOpen);
                  }}
                  style={
                    isMapviewOpen
                      ? { backgroundColor: mainColor || colors.primaryBlue }
                      : { backgroundColor: colors.borderLightgray }
                  }
                ></div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterBar;
